import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BRAND } from "@/lib/brand";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { ScrollToTop } from "@/components/ui/scroll-to-top";
import { cta, CTA_STYLES, CTA_COLORS } from "@/lib/cta";
import heroBackground from "@/assets/hero-background.jpg";

export default function SellersLanding() {
  const [lang, setLang] = useState<"en" | "fr">("en");
  const ctaTexts = cta("seller", lang);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <SiteHeader lang={lang} onToggleLang={() => setLang(lang === "en" ? "fr" : "en")} />

      {/* Hero */}
      <section
        className="w-full relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${BRAND.navy}, ${BRAND.navyDark})` }}
      >
        <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
          <img src={heroBackground} alt="" className="min-w-full min-h-full object-cover blur-[2px] opacity-45" />
        </div>
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(180deg, ${BRAND.navy}CC, ${BRAND.navyDark}CC)` }}
        ></div>
        <div className="relative mx-auto max-w-7xl px-4 py-10">
          <p className="text-sm uppercase" style={{ color: BRAND.silver }}>
            {lang === "fr" ? "Vendeurs" : "Sellers"}
          </p>
          <h1 className="mt-2 text-4xl md:text-5xl font-extrabold text-white">
            {lang === "fr" ? "Vendez votre entreprise avec AcquizGroup" : "Sell your business with AcquizGroup"}
          </h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg text-white/85">
            {lang === "fr"
              ? "Acheteurs qualifiés, confidentialité et workflows standardisés — de la NDA à la LOI, au financement et au closing."
              : "Qualified buyers, confidentiality, and standardized workflows — from NDA to LOI to Financing and Closing."}
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/register/sellers"
              className={CTA_STYLES.primary}
              style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}
            >
              {ctaTexts.primary}
            </Link>
            <Link
              to="/contact"
              aria-label={ctaTexts.aria}
              className={`${CTA_STYLES.secondary} border-white/30 text-white`}
            >
              {ctaTexts.secondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Savings banner - Moved under hero */}
      <section className="mx-auto max-w-7xl px-4 py-8">
        <div
          className="relative rounded-2xl p-8 overflow-hidden shadow-md border-2"
          style={{
            background: `linear-gradient(135deg, ${BRAND.navy}15 0%, ${BRAND.goldStart}10 100%)`,
            borderColor: BRAND.goldStart,
          }}
        >
          <div
            className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20"
            style={{ background: `linear-gradient(135deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}
          />

          <div className="relative flex items-start gap-4">
            <div
              className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-sm"
              style={{ background: `linear-gradient(135deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}
            >
              <svg className="w-6 h-6 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                {/* Colonne 1: 97% */}
                <div className="flex flex-col items-center">
                  <span className="text-3xl font-bold leading-tight" style={{ color: BRAND.navy }}>
                    {lang === "fr" ? "Jusqu'à" : "Up to"} 97%
                  </span>
                  <span className="text-3xl font-bold mt-1" style={{ color: BRAND.graphite }}>
                    {lang === "fr" ? "d'économies" : "savings"}
                  </span>
                  <span className="text-sm mt-1" style={{ color: BRAND.graphite }}>
                    {lang === "fr" ? "sur évaluation & mise en marché" : "on evaluation & go‑to‑market"}
                  </span>
                </div>

                {/* Signe + */}
                <span className="text-3xl font-bold" style={{ color: BRAND.graphite }}>
                  +
                </span>

                {/* Colonne 2: 1% */}
                <div className="flex flex-col items-center">
                  <span className="text-3xl font-bold leading-tight" style={{ color: BRAND.navy }}>
                    1%
                  </span>
                  <span className="text-3xl font-bold mt-1" style={{ color: BRAND.graphite }}>
                    {lang === "fr" ? "Seulement" : "Only"}
                  </span>
                  <span className="text-sm mt-1" style={{ color: BRAND.graphite }}>
                    {lang === "fr" ? "de frais de gestion" : "of management fees"}
                  </span>
                </div>

                {/* Signe = */}
                <span className="text-3xl font-bold" style={{ color: BRAND.graphite }}>
                  =
                </span>

                {/* Texte final */}
                <p className="text-4xl font-bold max-w-md" style={{ color: BRAND.graphite }}>
                  {lang === "fr"
                    ? "Pour que vous gardiez une plus grande part de vos revenus."
                    : "So you keep more in your pocket."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-semibold text-center" style={{ color: BRAND.navy }}>
          {lang === "fr" ? "Pourquoi choisir AcquizGroup" : "Why choose AcquizGroup"}
        </h2>
        <p className="mt-2 text-base md:text-lg text-center" style={{ color: BRAND.graphite }}>
          {lang === "fr"
            ? "Gardez une plus grande part de ce que vous avez bâti. Notre modèle vous aide à réduire les coûts de mise en marché et les frais de closing tout en protégeant la confidentialité et la rapidité."
            : "Keep more of what you built. Our model helps you reduce go‑to‑market costs and closing fees while protecting confidentiality and speed."}
        </p>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {[
            {
              h: lang === "fr" ? "Confidentialité absolue" : "Absolute confidentiality",
              p:
                lang === "fr"
                  ? "Vos informations restent protégées à chaque étape."
                  : "Your information stays protected at every step.",
            },
            {
              h: lang === "fr" ? "Réseau d'acheteurs qualifiés" : "Qualified buyer network",
              p:
                lang === "fr"
                  ? "Accédez à notre base exclusive d'investisseurs sérieux."
                  : "Gain access to our exclusive database of serious investors.",
            },
            {
              h: lang === "fr" ? "Dossiers prêts pour les prêteurs" : "Lender‑ready files",
              p:
                lang === "fr"
                  ? "Un packaging adapté aux prêteurs accélère les décisions."
                  : "Packaging built for lenders accelerates decisions.",
            },
          ].map((c, i) => (
            <div key={i} className="rounded-2xl border bg-white p-6 shadow-sm">
              <h3 className="font-semibold" style={{ color: BRAND.navy }}>
                {c.h}
              </h3>
              <p className="mt-1 text-sm" style={{ color: BRAND.graphite }}>
                {c.p}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-semibold" style={{ color: BRAND.navy }}>
          {lang === "fr" ? "Comment ça fonctionne" : "How it works"}
        </h2>
        <div className="mt-4 flex flex-wrap justify-center items-center gap-2">
          {(lang === "fr"
            ? [
                "Évaluation initiale",
                "Préparation du dossier",
                "Mise en marché",
                "Pré-financement",
                "Sélection acheteurs",
                "Closing",
              ]
            : ["Initial assessment", "File preparation", "Go‑to‑market", "Pre-financing", "Buyer selection", "Closing"]
          ).map((t, i, arr) => (
            <div
              key={i}
              className="relative px-8 py-5 text-base font-semibold text-center shadow-md transition-transform hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.navyDark})`,
                color: "white",
                clipPath:
                  i === 0
                    ? "polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%)"
                    : "polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%, 15% 50%)",
                minWidth: "180px",
              }}
            >
              <span className="relative z-10">{t}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 text-base md:text-lg" style={{ color: BRAND.graphite }}>
          {lang === "fr"
            ? "Après notre évaluation et approbation, votre entreprise est listée sur la plateforme et automatiquement envoyée aux institutions financières correspondantes pour simplifier et accélérer le processus de vente."
            : "After our evaluation and approval, your business is listed on the platform and automatically sent to matched financial institutions to simplify and speed up the sale process."}
        </div>

        {/* Learn More Button */}
        <div className="mt-8 text-center">
          <Link
            to="/general-landing"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-lg"
            style={{
              background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})`,
              color: BRAND.navy,
            }}
          >
            {lang === "fr" ? "En savoir plus" : "Learn more"}
          </Link>
        </div>
      </section>

      {/* Your Dream Team: Consultants & Experts */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8" style={{ color: BRAND.navy }}>
          {lang === "fr" ? "Votre Dream Team (Consultants vs. Experts)" : "Your Dream Team (Consultants vs. Experts)"}
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border bg-gradient-to-br from-white to-blue-50/50 p-6 shadow-sm">
            <h3 className="font-semibold" style={{ color: BRAND.navy }}>
              {lang === "fr" ? "Le Programme Consultant (Stratégie & Profil)" : "The Consultant Program (Strategy & Profile)"}
            </h3>
            <p className="mt-2 text-sm" style={{ color: BRAND.graphite }}>
              {lang === "fr"
                ? "Renforcez votre crédibilité auprès des vendeurs et des banques en collaborant avec des conseillers ou partenaires commerciaux possédant les licences et l'expertise sectorielle qui complètent votre profil."
                : "Strengthen your credibility with sellers and banks by collaborating with advisors or business partners who possess the licenses and industry expertise that complement your profile."}
            </p>
          </div>
          <div className="rounded-2xl border bg-gradient-to-br from-white to-blue-50/50 p-6 shadow-sm">
            <h3 className="font-semibold" style={{ color: BRAND.navy }}>
              {lang === "fr" ? "Le Programme Experts (Transactionnel)" : "The Experts Program (Transactional)"}
            </h3>
            <p className="mt-2 text-sm" style={{ color: BRAND.graphite }}>
              {lang === "fr"
                ? "Accédez directement à nos spécialistes internes — avocats d'affaires et en immigration, notaires, CPA et fiscalistes — pour une due diligence et un closing sans accroc."
                : "Gain direct access to our in-house specialists—business and immigration lawyers, notaries, CPAs, and tax professionals—for seamless due diligence and closing."}
            </p>
          </div>
        </div>

        {/* Learn More Button */}
        <div className="mt-8 text-center">
          <Link
            to="/general-landing"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-lg"
            style={{
              background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})`,
              color: BRAND.navy,
            }}
          >
            {lang === "fr" ? "En savoir plus" : "Learn more"}
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="text-2xl md:text-3xl font-semibold mb-6" style={{ color: BRAND.navy }}>
            {lang === "fr" ? "Questions fréquentes" : "Frequently Asked Questions"}
          </h2>
          <div className="space-y-4">
            {[
              {
                q: lang === "fr" ? "Combien coûte la mise en marché ?" : "How much does go-to-market cost?",
                a:
                  lang === "fr"
                    ? "Nous offrons jusqu'à 97% d'économies sur l'évaluation et la mise en marché, avec seulement 1% de frais de gestion de plateforme."
                    : "We offer up to 97% savings on evaluation and go-to-market, with only 1% platform management fee.",
              },
              {
                q: lang === "fr" ? "Comment fonctionne la confidentialité ?" : "How does confidentiality work?",
                a:
                  lang === "fr"
                    ? "Vos informations restent protégées à chaque étape. Personne ne peut voir vos données confidentielles jusqu'à la signature d'un NDA numérique sur la plateforme."
                    : "Your information stays protected at every step. No one can see your confidential data until a digital NDA is signed on the platform.",
              },
              {
                q: lang === "fr" ? "Qui sont les acheteurs potentiels ?" : "Who are the potential buyers?",
                a:
                  lang === "fr"
                    ? "Accédez à notre base exclusive d'investisseurs sérieux et vérifiés, incluant des acheteurs locaux et internationaux qualifiés."
                    : "Access our exclusive pool of serious, verified investors, including qualified local and international buyers.",
              },
              {
                q:
                  lang === "fr"
                    ? "Combien de temps prend le processus de vente ?"
                    : "How long does the sale process take?",
                a:
                  lang === "fr"
                    ? "Grâce à nos dossiers prêts pour les prêteurs et notre pré-financement, nous accélérons significativement les délais de closing."
                    : "Thanks to our lender-ready files and pre-financing, we significantly accelerate closing timelines.",
              },
              {
                q: lang === "fr" ? "Puis-je utiliser mes propres conseillers ?" : "Can I use my own advisors?",
                a:
                  lang === "fr"
                    ? "Oui, vous êtes libre d'utiliser vos propres conseillers. Cependant, nos experts sont formés aux workflows de la plateforme pour accélérer le closing."
                    : "Yes, you're free to use your own advisors. However, our experts are trained on the platform workflows to accelerate closing.",
              },
            ].map((faq, i) => (
              <details key={i} className="rounded-xl border p-4 bg-gray-50">
                <summary className="font-medium cursor-pointer" style={{ color: BRAND.navy }}>
                  {faq.q}
                </summary>
                <p className="mt-2 text-base" style={{ color: BRAND.graphite }}>
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div
          className="rounded-2xl p-8 text-center shadow-lg"
          style={{ background: `linear-gradient(180deg, ${BRAND.navyDark}, ${BRAND.navy})` }}
        >
          <h2 className="text-2xl font-semibold text-white">
            {lang === "fr" ? "Prêt à vendre votre entreprise ?" : "Ready to sell your business?"}
          </h2>
          <p className="mt-2 text-sm" style={{ color: BRAND.silver }}>
            {lang === "fr"
              ? "Listez en toute confiance, rejoignez des acheteurs qualifiés et gardez plus des revenus que vous avez gagnés."
              : "List with confidence, reach qualified buyers, and keep more of the proceeds you earned."}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/sellerplan"
              className="rounded-2xl border-2 px-5 py-2 text-sm font-semibold transition-all hover:shadow-lg"
              style={{
                borderColor: BRAND.goldStart,
                color: BRAND.goldStart,
              }}
            >
              {lang === "fr" ? "Plans Vendeur" : "Seller Plans"}
            </Link>
            <Link
              to="/register/sellers"
              className={CTA_STYLES.primary}
              style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}
            >
              {ctaTexts.primary}
            </Link>
            <Link
              to="/contact"
              aria-label={ctaTexts.aria}
              className={`${CTA_STYLES.secondary} border-white/30 text-white`}
            >
              {ctaTexts.secondary}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter lang={lang} />
      <ScrollToTop />
    </div>
  );
}
